import { Lexer, Marked, Tokens } from 'marked';

/** The authoring notes at the end of a manual, which the help leaves out. */
const SCREENSHOTS_SECTION = /^## Schermafbeeldingen voor dit artikel\s*$/m;

/** The anchor of a heading, like on Bitbucket and GitHub, so the links between the manuals keep working. */
export function headingSlug(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{N}\s_-]/gu, '')
    .replace(/\s/g, '-');
}

/** The sections every manual has, which the table of contents leaves out (in Dutch and French). */
const GENERAL_SECTION =
  /^(Waarvoor dient|Voor je begint|Verwante onderwerpen|À quoi sert|Avant de commencer|Sujets connexes)/;

/**
 * A heading can set its own anchor at its end: `## Commandes {#orders}`. The translated manuals use it to keep the
 * anchors of the Dutch ones, so the links from the screens and between the manuals work in every language.
 */
const EXPLICIT_ID = /\s*\{#([\p{L}\p{N}_-]+)\}\s*$/u;

/** The text of a heading without its own anchor, and that anchor if it has one. */
function splitHeading(text: string): { text: string; id?: string } {
  const match = text.match(EXPLICIT_ID);
  return match ? { text: text.slice(0, match.index), id: match[1] } : { text };
}

/** A section (`## …`) of a manual, with its anchor. */
export interface HelpSection {
  id: string;
  title: string;
}

/**
 * Gives every heading of a manual its anchor in order, numbering the anchors of headings that repeat; a heading with its
 * own anchor keeps it.
 */
function slugger(): (text: string) => string {
  const slugs = new Map<string, number>();
  return (heading) => {
    const { text, id } = splitHeading(heading);
    if (id) return id;
    const slug = headingSlug(text);
    const count = slugs.get(slug) ?? 0;
    slugs.set(slug, count + 1);
    return count ? `${slug}-${count}` : slug;
  };
}

const withoutScreenshotNotes = (markdown: string) => markdown.split(SCREENSHOTS_SECTION)[0];
const plainText = (markdown: string) => markdown.replace(/[*_`]/g, '').trim();

/** The title of a manual: its `# ` heading, without its own anchor. */
export function helpTitle(markdown: string): string | null {
  const heading = markdown.match(/^# (.+)$/m)?.[1];
  return heading ? plainText(splitHeading(heading).text) : null;
}

/** The sections of a manual a user would look for, i.e. without the introduction and the related topics. */
export function helpSections(markdown: string): HelpSection[] {
  const anchor = slugger();
  const sections: HelpSection[] = [];
  new Lexer().lex(withoutScreenshotNotes(markdown)).forEach((token) =>
    walkHeadings(token, (heading) => {
      const id = anchor(heading.text);
      const { text } = splitHeading(heading.text);
      if (heading.depth === 2 && !GENERAL_SECTION.test(text)) sections.push({ id, title: plainText(text) });
    }),
  );
  return sections;
}

/** The headings in the order the renderer meets them (also those in lists and quotes). */
function walkHeadings(
  token: { type: string; tokens?: unknown[]; items?: unknown[] },
  visit: (heading: Tokens.Heading) => void,
) {
  if (token.type === 'heading') visit(token as Tokens.Heading);
  for (const child of [...(token.items ?? []), ...(token.tokens ?? [])] as { type: string }[])
    walkHeadings(child, visit);
}

/** The open questions in the manuals (`> **TODO (verify):** …`), for the authors, not for the users. */
const AUTHOR_NOTE = /^\s*\**TODO\b/;

const isRelative = (href: string) => !/^([a-z][a-z0-9+.-]*:|\/|#)/i.test(href);
const escapeAttribute = (value: string) => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

const escapeText = (value: string) => value.replace(/&/g, '&amp;').replace(/</g, '&lt;');

/** The links to the sections of a manual, above its first section. */
function tableOfContents(sections: HelpSection[], label: string): string {
  if (sections.length < 2) return '';
  const links = sections.map(
    (section) => `<li><a href="#${escapeAttribute(section.id)}">${escapeText(section.title)}</a></li>`,
  );
  return `<nav class="help-toc"><p class="help-toc__label">${escapeText(label)}</p><ul>${links.join('')}</ul></nav>\n`;
}

/**
 * A manual (markdown) as html: headings get their anchor, images come from `basePath` next to the manual and the
 * screenshot notes at the end and the open questions of the authors are left out. With a `tocLabel`, the links to its
 * sections come before the first section. Links stay as they are; the help panel opens them.
 */
export function renderHelp(markdown: string, basePath: string, tocLabel?: string): string {
  const anchor = slugger();
  let toc = tocLabel ? tableOfContents(helpSections(markdown), tocLabel) : '';
  const marked = new Marked({
    gfm: true,
    renderer: {
      heading({ tokens, depth, text }) {
        const content = this.parser.parseInline(tokens).replace(EXPLICIT_ID, '');
        const heading = `<h${depth} id="${escapeAttribute(anchor(text))}">${content}</h${depth}>\n`;
        if (depth !== 2 || !toc) return heading;
        const before = toc;
        toc = '';
        return before + heading;
      },
      blockquote({ text, tokens }) {
        return AUTHOR_NOTE.test(text) ? '' : `<blockquote>\n${this.parser.parse(tokens)}</blockquote>\n`;
      },
      image({ href, text }) {
        const src = isRelative(href) ? `${basePath}/${href}` : href;
        return `<img src="${escapeAttribute(src)}" alt="${escapeAttribute(text)}" loading="lazy">`;
      },
    },
  });
  return marked.parse(withoutScreenshotNotes(markdown), { async: false });
}
